import { clampDateTo, FLEET_COST_CATEGORIES, type FleetCostReportDto, formatMoney, type MeDto, shiftDate, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Button, Card, Group, Select, SimpleGrid, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { VehicleStatusBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard, StatCard, StatGrid } from '../../components/ui';
import { monthPl, qtyPl } from '../../format';
import { FleetShell, kmPl } from './common';
import { useMyCompanies } from './VehiclesPage';
import { IconCash, IconGasStation, IconReceipt, IconCalculator } from '@tabler/icons-react';

const monthStart = (d: string) => `${d.slice(0, 7)}-01`;

/** Raport kosztów floty: z faktur, ręczne i przewidywane osobno (suma = z faktur + ręczne), paliwo, koszt km. */
export function FleetCostsPage({ me }: { me: MeDto }) {
  const today = todayInWarsaw();
  const companies = useMyCompanies(me);
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const params = new URLSearchParams({ from, to });
  if (companyId) params.set('companyId', companyId);
  const q = useQuery({ queryKey: ['fleet', 'cost-report', params.toString()], queryFn: () => api<FleetCostReportDto>(`/fleet/costs/report?${params}`), enabled: !!from && !!to && to >= from });
  const d = q.data;
  const totals = (d?.companies ?? []).reduce((a, c) => ({ total: a.total + c.totalNetGr, invoice: a.invoice + c.invoiceNetGr, manual: a.manual + c.manualNetGr, planned: a.planned + c.plannedNetGr }), { total: 0, invoice: 0, manual: 0, planned: 0 });
  const liters = (d?.vehicles ?? []).reduce((a, v) => a + v.fuelLiters, 0);
  const fuelGr = (d?.vehicles ?? []).reduce((a, v) => a + v.fuelNetGr, 0);
  const categories = Object.keys(FLEET_COST_CATEGORIES).filter((k) => (d?.vehicles ?? []).some((v) => (v.byCategory[k] ?? 0) !== 0));
  const vehicles = (d?.vehicles ?? []).filter((v) => v.totalNetGr || v.plannedNetGr || v.fuelLiters);

  return (
    <FleetShell me={me}>
      <PageHeader title="Koszty floty" description="Kwoty netto. Suma = koszty z faktur + ręczne; przewidywane pokazujemy osobno i nie doliczamy. Koszt km — tylko przy potwierdzonych odczytach przebiegu blisko początku i końca okresu." />
      <Group mb="md" gap="xs" wrap="wrap" align="flex-end">
        <TextInput label="Od" type="date" value={from} onChange={(e) => {
          const f = e.currentTarget.value;
          setFrom(f);
          setTo((t) => clampDateTo(f, t));
        }} w={{ base: '100%', sm: 170 }} />
        <TextInput label="Do" type="date" min={from || undefined} value={to} onChange={(e) => setTo(e.currentTarget.value)} w={{ base: '100%', sm: 170 }} />
        <Button variant="default" onClick={() => (setFrom(monthStart(today)), setTo(today))}>
          Bieżący miesiąc
        </Button>
        <Button
          variant="default"
          onClick={() => {
            const end = shiftDate(monthStart(today), -1);
            setFrom(monthStart(end));
            setTo(end);
          }}
        >
          Poprzedni miesiąc
        </Button>
        <Button variant="default" onClick={() => (setFrom(`${today.slice(0, 4)}-01-01`), setTo(today))}>
          Bieżący rok
        </Button>
        {companies.length > 1 && <Select label="Firma" placeholder="Wszystkie firmy" clearable data={companies.map((c) => ({ value: c.id, label: c.shortName }))} value={companyId} onChange={setCompanyId} w={{ base: '100%', sm: 200 }} />}
      </Group>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <Stack gap="md">
            <StatGrid>
              <StatCard label="Suma (z faktur + ręczne)" value={<Money gr={totals.total} />} icon={<IconCash size={22} />} />
              <StatCard label="Z faktur" value={<Money gr={totals.invoice} />} icon={<IconReceipt size={22} />} hint={`ręczne: ${formatMoney(totals.manual)}`} />
              <StatCard label="Przewidywane (poza sumą)" value={<Money gr={totals.planned} />} icon={<IconCalculator size={22} />} color="gray" />
              <StatCard label="Paliwo" value={<Money gr={fuelGr} />} icon={<IconGasStation size={22} />} hint={`${qtyPl(Math.round(liters * 100) / 100)} l`} />
            </StatGrid>
            {d.excludedRejectedGr !== 0 && (
              <Alert color="gray" variant="light" py="xs">
                Poza sumami: <Money gr={d.excludedRejectedGr} /> z faktur odrzuconych w Finansach.
              </Alert>
            )}
            <SectionCard title="Pojazdy" padding={0}>
              {vehicles.length ? (
                <Table.ScrollContainer minWidth={1000}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Pojazd</Table.Th>
                        <Table.Th ta="right">Suma</Table.Th>
                        <Table.Th ta="right">Z faktur</Table.Th>
                        <Table.Th ta="right">Ręczne</Table.Th>
                        <Table.Th ta="right">Przewidywane</Table.Th>
                        <Table.Th ta="right">Paliwo (l)</Table.Th>
                        <Table.Th ta="right">Paliwo</Table.Th>
                        <Table.Th ta="right">Przebieg</Table.Th>
                        <Table.Th ta="right">Koszt km</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {vehicles.map((v) => (
                        <Table.Tr key={v.vehicleId}>
                          <Table.Td>
                            <Anchor component={Link} to={`/flota/pojazdy/${v.vehicleId}?zakladka=koszty`} size="sm" fw={600}>
                              {v.plate}
                            </Anchor>{' '}
                            {v.status === 'wycofany' && <VehicleStatusBadge status={v.status} size="xs" />}
                            <Text size="xs" c="dimmed">
                              {v.companyName}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right" fw={600}>
                            <Money gr={v.totalNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">
                            <Money gr={v.invoiceNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">
                            <Money gr={v.manualNetGr} />
                          </Table.Td>
                          <Table.Td ta="right" c="dimmed">
                            <Money gr={v.plannedNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">{v.fuelLiters ? qtyPl(Math.round(v.fuelLiters * 100) / 100) : '—'}</Table.Td>
                          <Table.Td ta="right">
                            <Money gr={v.fuelNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">{v.km !== null ? kmPl(v.km) : '—'}</Table.Td>
                          <Table.Td ta="right">
                            {v.costPerKmGr !== null ? (
                              <Money gr={v.costPerKmGr} />
                            ) : (
                              <Tooltip label={v.kmNote || 'brak potwierdzonych odczytów na początku i końcu okresu'} multiline w={260}>
                                <Text size="xs" c="dimmed">
                                  brak danych
                                </Text>
                              </Tooltip>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : (
                <EmptyState compact title="Brak kosztów w wybranym okresie" />
              )}
            </SectionCard>
            {categories.length > 0 && (
              <SectionCard title="Podział na kategorie (z faktur + ręczne)" padding={0}>
                <Table.ScrollContainer minWidth={Math.max(600, 160 + categories.length * 120)}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Pojazd</Table.Th>
                        {categories.map((c) => (
                          <Table.Th key={c} ta="right">
                            {FLEET_COST_CATEGORIES[c as keyof typeof FLEET_COST_CATEGORIES]}
                          </Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {vehicles.map((v) => (
                        <Table.Tr key={v.vehicleId}>
                          <Table.Td fw={500}>{v.plate}</Table.Td>
                          {categories.map((c) => (
                            <Table.Td key={c} ta="right">
                              {v.byCategory[c] ? <Money gr={v.byCategory[c]} /> : '—'}
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                      <Table.Tr bg="var(--app-subtle)">
                        <Table.Td fw={700}>Razem</Table.Td>
                        {categories.map((c) => (
                          <Table.Td key={c} ta="right" fw={700}>
                            <Money gr={vehicles.reduce((a, v) => a + (v.byCategory[c] ?? 0), 0)} />
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </SectionCard>
            )}
            {d.months.length > 1 && vehicles.length > 0 && (
              <SectionCard title="Miesiące (z faktur + ręczne)" padding={0}>
                <Table.ScrollContainer minWidth={Math.max(600, 160 + d.months.length * 120)}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Pojazd</Table.Th>
                        {d.months.map((m) => (
                          <Table.Th key={m} ta="right">
                            {monthPl(`${m.slice(0, 7)}-01`)}
                          </Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {vehicles.map((v) => (
                        <Table.Tr key={v.vehicleId}>
                          <Table.Td fw={500}>{v.plate}</Table.Td>
                          {d.months.map((m) => (
                            <Table.Td key={m} ta="right">
                              {v.byMonth[m] ? <Money gr={v.byMonth[m]} /> : '—'}
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </SectionCard>
            )}
            {d.companies.length > 0 && (
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <SectionCard title="Koszty floty wg firmy" padding={0}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Firma</Table.Th>
                        <Table.Th ta="right">Suma</Table.Th>
                        <Table.Th ta="right">Z faktur</Table.Th>
                        <Table.Th ta="right">Ręczne</Table.Th>
                        <Table.Th ta="right">Przewidywane</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {d.companies.map((c) => (
                        <Table.Tr key={c.companyId}>
                          <Table.Td>{c.companyName}</Table.Td>
                          <Table.Td ta="right" fw={600}>
                            <Money gr={c.totalNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">
                            <Money gr={c.invoiceNetGr} />
                          </Table.Td>
                          <Table.Td ta="right">
                            <Money gr={c.manualNetGr} />
                          </Table.Td>
                          <Table.Td ta="right" c="dimmed">
                            <Money gr={c.plannedNetGr} />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </SectionCard>
              </SimpleGrid>
            )}
          </Stack>
        )}
        {!d && to < from && (
          <Card>
            <EmptyState compact title="Data „do” jest wcześniejsza niż „od”" />
          </Card>
        )}
      </QueryState>
    </FleetShell>
  );
}
